import { type FC } from 'react';
import slugify from 'dibs-slugify/exports/slugify';
import { graphql, useFragment } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { type ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

// components
import { FormattedMessage } from 'dibs-react-intl';
import {
    PdpSharedDetail,
    type PdpSharedDetailProps,
} from '../../shared/PdpSharedDetail/PdpSharedDetail';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import { PdpSharedDetailRingSize } from '../../shared/PdpSharedDetail/PdpSharedDetailRingSize';
import { PdpSharedDetailMovement } from '../../shared/PdpSharedDetail/PdpSharedDetailMovement';
import { PdpSharedDetailLabReport } from '../../shared/PdpSharedDetail/PdpSharedDetailLabReport';
import { PdpSharedDetailCondition } from '../../shared/PdpSharedDetail/PdpSharedDetailCondition';
import { PdpSharedDetailReference } from '../../shared/PdpSharedDetail/PdpSharedDetailReference';
import { PdpSharedDetailFramingOptions } from '../../shared/PdpSharedDetail/PdpSharedDetailFramingOptions';

// styles
import styles from './PdpRespSharedItemDetailsList.scss';

// types
import {
    type PdpRespSharedItemDetailsList_item$data,
    type PdpRespSharedItemDetailsList_item$key as Item,
} from './__generated__/PdpRespSharedItemDetailsList_item.graphql';
import { type PdpRespSharedItemDetailsList_user$key as User } from './__generated__/PdpRespSharedItemDetailsList_user.graphql';

type PdpDetailsDisplayArray = NonNullable<
    NonNullable<PdpRespSharedItemDetailsList_item$data>['pdpDetailsDisplay']
>;
type PdpDetailsDisplay = NonNullable<ArrayElement<PdpDetailsDisplayArray>>;

type Props = {
    item: Item;
    currency: string;
    user: User | null | undefined;
    showHeader?: boolean;
    selectedSkuId?: string | null;
};

const detailsToIgnore = ['FRAMING_STATUS'];

const notDisplayableDetails = (detail: PdpDetailsDisplay): boolean =>
    !detailsToIgnore.includes(detail?.detailKey || '');

const getFramingDetails = (details: PdpDetailsDisplayArray): PdpDetailsDisplay | undefined | null =>
    details?.find(detail => detail?.detailKey === 'FRAMING_STATUS');

const itemFragment = graphql`
    fragment PdpRespSharedItemDetailsList_item on Item {
        serviceId
        pdpDetailsDisplay {
            detailKey
            delimiter
            detailDisplayName
            detailValues {
                ...PdpSharedDetail_detailValues
                ...PdpSharedDetailRingSize_detailValues
                ...PdpSharedDetailMovement_detailValues
            }
        }
        ...PdpSharedDetailReference_item
        ...PdpSharedDetailCondition_item
        ...PdpSharedDetailFramingOptions_refactor_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSharedItemDetailsList_user on User {
        ...PdpSharedDetailReference_user
        ...PdpSharedDetailFramingOptions_refactor_user
    }
`;

export const PdpRespSharedItemDetailsList: FC<Props> = ({
    currency,
    item: itemRef,
    user: userRef,
    selectedSkuId,
    showHeader = true,
}) => {
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);

    const { serviceId, pdpDetailsDisplay } = item;
    const details = pdpDetailsDisplay || [];

    return (
        <div className={styles.wrapper} data-tn="pdp-details">
            {showHeader && (
                <HeadingLevel>
                    {Heading => (
                        <Heading className={styles.header}>
                            <FormattedMessage
                                defaultMessage="Details"
                                id="PdpRespSharedDetailsList.detailsTitle"
                            />
                        </Heading>
                    )}
                </HeadingLevel>
            )}
            <ul className={styles.detailsWrapper}>
                {details
                    .filter(filterNulls)
                    .filter(notDisplayableDetails)
                    .map((detail, index) => {
                        const {
                            detailKey,
                            delimiter,
                            detailDisplayName,
                            detailValues: unfilteredDetailValues,
                        } = detail;
                        const detailValues = (unfilteredDetailValues || []).filter(filterNulls);

                        const sharedProps = {
                            currency,
                            delimiter,
                            detailValues,
                            selectedSkuId,
                            displayValuesAs:
                                'inline-block' as PdpSharedDetailProps['displayValuesAs'],
                        };

                        let Content = null;
                        if (detailKey === 'REFERENCE_NUMBER') {
                            Content = (
                                <PdpSharedDetailReference
                                    {...sharedProps}
                                    item={item}
                                    user={user}
                                    displayValuesAs="block"
                                />
                            );
                        } else if (detailKey === 'FRAMING') {
                            const frameIncludedDetails = getFramingDetails(details);
                            Content = (
                                <>
                                    {!!frameIncludedDetails && (
                                        <div>{frameIncludedDetails.detailDisplayName}</div>
                                    )}
                                    <PdpSharedDetailFramingOptions
                                        {...sharedProps}
                                        item={item}
                                        user={user}
                                    />
                                </>
                            );
                        } else if (detailKey === 'CONDITION' && serviceId) {
                            Content = <PdpSharedDetailCondition {...sharedProps} item={item} />;
                        } else if (detailKey === 'MOVEMENT') {
                            Content = <PdpSharedDetailMovement {...sharedProps} />;
                        } else if (detailKey === 'RING_SIZE') {
                            Content = (
                                <PdpSharedDetailRingSize
                                    currency={currency}
                                    detailValues={detailValues}
                                />
                            );
                        } else if (detailKey === 'LAB_REPORT') {
                            Content = <PdpSharedDetailLabReport {...sharedProps} />;
                        } else {
                            Content = <PdpSharedDetail {...sharedProps} />;
                        }

                        return (
                            <li className={styles.list} key={`${detailKey}-${index}`}>
                                <div className={styles.listTitle} data-tn="pdp-spec-title">
                                    <FormattedMessage
                                        id="PdpRespSharedItemDetailsList.itemTitle"
                                        defaultMessage="{detailDisplayName}:"
                                        values={{ detailDisplayName }}
                                    />
                                </div>
                                <div
                                    className={styles.listContent}
                                    data-tn={`pdp-spec-${slugify(detailDisplayName)}`}
                                >
                                    {Content}
                                </div>
                            </li>
                        );
                    })}
            </ul>
        </div>
    );
};
